<template>
  <div class="application-step" :class="className">
    <component v-if="componentName" :is="componentName" v-bind="currentProperties" v-on="currentListeners"></component>
    <slot v-else></slot>
    <Feedback @showSupportModal="showSupportModal = true"></Feedback>

    <transition name="modal">
      <support-modal id="supportModal" @close="showSupportModal = false" v-if="showSupportModal" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref, computed } from "vue";
import { ProductIds } from "@/config/constants";
import { Programs } from "@/config/enums";
import Feedback from "@/components/shared/feedback/Feedback.vue";
import SupportModal from "@/components/modals/SupportModal.vue";
import { useStore } from "vuex";
import useApplicationData from "@/composables/application/useApplicationData";

const ChoosePath = defineAsyncComponent(() => import("../application/ChoosePath.vue"));
const SchoolInfo = defineAsyncComponent(() => import("../application/SchoolInfo.vue"));
const DgSchoolInfo = defineAsyncComponent(() => import("../application/DgSchoolInfo.vue"));
const PersonalInfoPartOne = defineAsyncComponent(() => import("../application/PersonalInfoPartOne.vue"));
const PersonalInfoPartTwo = defineAsyncComponent(() => import("../application/PersonalInfoPartTwo.vue"));
const ShowMyRates = defineAsyncComponent(() => import("../application/ShowMyRates.vue"));
const PelShowMyRates = defineAsyncComponent(() => import("../application/PelShowMyRates.vue"));
const Registration = defineAsyncComponent(() => import("../application/Registration.vue"));
const AdditionalInfo = defineAsyncComponent(() => import("../application/AdditionalInfo.vue"));
const DgAdditionalInfo = defineAsyncComponent(() => import("../application/DgAdditionalInfo.vue"));
const RefiAdditionalInfo = defineAsyncComponent(() => import("../application/RefiAdditionalInfo.vue"));
const RefiConsent = defineAsyncComponent(() => import("../application/RefiConsent.vue"));
const Consent = defineAsyncComponent(() => import("../shared/Consent.vue"));
const IsPresentInfoPartOne = defineAsyncComponent(() => import("../application/IsPresentInfoPartOne.vue"));
const IsPresentInfoPartTwo = defineAsyncComponent(() => import("../application/IsPresentInfoPartTwo.vue"));
const AddCoApplicant = defineAsyncComponent(() => import("../application/AddCoApplicant.vue"));
const Approval = defineAsyncComponent(() => import("../application/Approval.vue"));
const LoanConfiguration = defineAsyncComponent(() => import("../application/LoanConfiguration.vue"));
const PelLoanConfiguration = defineAsyncComponent(() => import("../application/PelLoanConfiguration.vue"));
const MaritalStatus = defineAsyncComponent(() => import("../application/MaritalStatus.vue"));
const InvitedApplicant = defineAsyncComponent(() => import("../application/InvitedApplicant.vue"));
const InvitedApplicantInfoPartOne = defineAsyncComponent(
  () => import("../application/InvitedApplicantInfoPartOne.vue"),
);
const InvitedApplicantInfoPartTwo = defineAsyncComponent(
  () => import("../application/InvitedApplicantInfoPartTwo.vue"),
);
const StudentInformation = defineAsyncComponent(() => import("../application/StudentInformation.vue"));

const props = defineProps({
  route: { type: String, required: true },
  application: { type: Object, required: true },
  path: { type: String, required: true },
  loading: { type: Boolean, required: true },
  previousLoading: { type: Boolean, required: true },
  isCurrent: { type: Boolean, required: true },
});

const emit = defineEmits(["previous", "complete"]);

const store = useStore();

const { isValidProfessionalGradFlow } = useApplicationData();

const showSupportModal = ref<boolean>(false);
const coApplicant = ref<string>("Co-Applicant");
const primaryApplicant = ref<string>("Primary Applicant");
const primaryApplicantDisplayName = ref<string>("Borrower");
const coApplicantDisplayName = computed<string>(() => store.getters["coApplicantTypeDisplayName"]);

const coApplicantRoutes = [
  "coborrowerPersonalInfo",
  "coborrowerPersonalInfoContinued",
  "coborrowerRegistration",
  "coborrowerAdditionalInfo",
  "dgCoborrowerAdditionalInfo",
  "coborrowerHardPullConsent",
  "coborrowerInfo",
  "coborrowerInfoContinued",
  "coborrowerMaritalStatus",
  "coborrowerInvited",
  "coborrowerInvitedApplicantInfo",
  "coborrowerInvitedApplicantInfoContinued",
];

const productTypeId = computed<number>(() => props.application.application.productTypeId);

const currentRoute = computed(() => props.route);

const componentName = computed(() => {
  return getRouteComponent();
});

const currentProperties = computed(() => {
  switch (currentRoute.value) {
    case "chooseBorrowerCoborrower":
    case "dgChooseBorrowerCoborrower":
    case "approval":
    case "dgApproval":
      return {
        loading: props.loading,
        isCurrent: props.isCurrent,
      };
    case "loanConfiguration":
    case "dgLoanConfiguration":
      return {
        isCurrent: props.isCurrent,
      };
    case "schoolInformation":
    case "dgSchoolInformation":
    case "showMyRates":
    case "addCoborrower":
    case "dgAddCoborrower":
      return {
        loading: props.loading,
        previousLoading: props.previousLoading,
        isCurrent: props.isCurrent,
      };
    case "coborrowerPersonalInfo":
    case "coborrowerPersonalInfoContinued":
    case "coborrowerRegistration":
    case "coborrowerHardPullConsent":
    case "coborrowerInfo":
    case "coborrowerInfoContinued":
      return {
        loading: props.loading,
        previousLoading: props.previousLoading,
        isCurrent: props.isCurrent,
        applicantType: coApplicant.value,
        applicantTypeDisplayName: coApplicantDisplayName.value,
      };
    case "borrowerPersonalInfo":
    case "borrowerPersonalInfoContinued":
    case "borrowerInfo":
    case "borrowerInfoContinued":
    case "borrowerRegistration":
    case "borrowerHardPullConsent":
      return {
        loading: props.loading,
        previousLoading: props.previousLoading,
        isCurrent: props.isCurrent,
        applicantType: primaryApplicant.value,
        applicantTypeDisplayName: primaryApplicantDisplayName.value,
      };
    case "borrowerAdditionalInfo":
    case "dgBorrowerAdditionalInfo":
      return {
        loading: props.loading,
        previousLoading: props.previousLoading,
        isCurrent: props.isCurrent,
        applicantType: primaryApplicant.value,
        applicantTypeDisplayName: primaryApplicantDisplayName.value,
        path: props.path,
      };
    case "coborrowerAdditionalInfo":
    case "dgCoborrowerAdditionalInfo":
      return {
        loading: props.loading,
        previousLoading: props.previousLoading,
        isCurrent: props.isCurrent,
        applicantType: coApplicant.value,
        applicantTypeDisplayName: coApplicantDisplayName.value,
        path: props.path,
      };
    case "borrowerMaritalStatus":
    case "borrowerInvited":
    case "borrowerInvitedApplicantInfo":
    case "borrowerInvitedApplicantInfoContinued":
      return {
        loading: props.loading,
        isCurrent: props.isCurrent,
        applicantType: primaryApplicant.value,
        applicantTypeDisplayName: primaryApplicantDisplayName.value,
      };
    case "coborrowerMaritalStatus":
    case "coborrowerInvited":
    case "coborrowerInvitedApplicantInfo":
    case "coborrowerInvitedApplicantInfoContinued":
      return {
        loading: props.loading,
        isCurrent: props.isCurrent,
        applicantType: coApplicant.value,
        applicantTypeDisplayName: coApplicantDisplayName.value,
      };
    case "studentInformation":
      return {
        loading: props.loading,
        isCurrent: props.isCurrent,
        previousLoading: props.previousLoading,
        person: store.state.appData.application.student,
        personType: "Student",
        personTypeDisplay: "Student",
      };
    default:
      return {};
  }
});

const currentListeners = computed(() => {
  switch (componentName.value) {
    default:
      return {
        previous: previous,
        complete: complete,
      };
  }
});

function getRouteComponent() {
  switch (currentRoute.value) {
    case "chooseBorrowerCoborrower":
    case "dgChooseBorrowerCoborrower":
      return ChoosePath;
    case "schoolInformation":
      return SchoolInfo;
    case "dgSchoolInformation":
      return DgSchoolInfo;
    case "coborrowerPersonalInfo":
    case "borrowerPersonalInfo":
      return PersonalInfoPartOne;
    case "coborrowerPersonalInfoContinued":
    case "borrowerPersonalInfoContinued":
      return PersonalInfoPartTwo;
    case "showMyRates":
      if (productTypeId.value === ProductIds.PEL) {
        return PelShowMyRates;
      } else {
        return ShowMyRates;
      }
    case "coborrowerRegistration":
    case "borrowerRegistration":
      return Registration;
    case "borrowerAdditionalInfo":
    case "coborrowerAdditionalInfo":
      if (productTypeId.value === ProductIds.PEL || productTypeId.value === ProductIds.REFI) {
        return RefiAdditionalInfo;
      } else {
        return AdditionalInfo;
      }
    case "dgBorrowerAdditionalInfo":
    case "dgCoborrowerAdditionalInfo":
      if (
        productTypeId.value === ProductIds.PEL ||
        productTypeId.value === ProductIds.REFI ||
        (productTypeId.value === ProductIds.LOC && isValidProfessionalGradFlow.value)
      ) {
        return RefiAdditionalInfo;
      } else {
        return DgAdditionalInfo;
      }
    case "coborrowerHardPullConsent":
    case "borrowerHardPullConsent":
      if (productTypeId.value === ProductIds.PEL || productTypeId.value === ProductIds.REFI) {
        return RefiConsent;
      } else {
        return Consent;
      }
    case "borrowerInfo":
    case "coborrowerInfo":
      return IsPresentInfoPartOne;
    case "borrowerInfoContinued":
    case "coborrowerInfoContinued":
      return IsPresentInfoPartTwo;
    case "addCoborrower":
    case "dgAddCoborrower":
      return AddCoApplicant;
    case "approval":
    case "dgApproval":
      return Approval;
    case "loanConfiguration":
    case "dgLoanConfiguration":
      if (productTypeId.value === ProductIds.PEL) {
        return PelLoanConfiguration;
      } else {
        return LoanConfiguration;
      }
    case "coborrowerMaritalStatus":
    case "borrowerMaritalStatus":
      return MaritalStatus;
    case "borrowerInvited":
    case "coborrowerInvited":
      return InvitedApplicant;
    case "borrowerInvitedApplicantInfo":
    case "coborrowerInvitedApplicantInfo":
      return InvitedApplicantInfoPartOne;
    case "borrowerInvitedApplicantInfoContinued":
    case "coborrowerInvitedApplicantInfoContinued":
      return InvitedApplicantInfoPartTwo;
    case "studentInformation":
      return StudentInformation;
    default:
      return undefined;
  }
}

function previous(event: any) {
  emit("previous", event);
}

function complete(event: any) {
  emit("complete", event);
}

function className(route: string) {
  return coApplicantRoutes.includes(props.route) === true ? `${route} co-borrower` : `${route}`;
}
</script>

<style scoped>
.application-step {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

@media (max-width: 992px) {
  .application-step {
    justify-content: flex-start;
  }
}
</style>
